import { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";

const ACCENT = "#111827";

function RetryPaymentDialog({ title, orderNumber, status, onRetry, onResult }) {
  const [shown, setShown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const raf = requestAnimationFrame(() => setShown(true));
    const onKey = (e) => { if (e.key === "Escape" && !loading) onResult(false); };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onResult, loading]);

  const retry = async () => {
    if (loading) return;
    setError("");
    setLoading(true);
    try {
      await onRetry();
      // Обычно onRetry делает redirect на оплату и страница уходит.
      // Если redirect не произошёл — считаем успехом и закрываем окно.
      onResult(true);
    } catch (e) {
      setError(e?.message || "Не удалось перейти к оплате. Попробуйте ещё раз.");
      setLoading(false);
    }
  };

  return (
    <div
      onMouseDown={(e) => { if (e.target === e.currentTarget && !loading) onResult(false); }}
      style={{
        position: "fixed", inset: 0, zIndex: 100000,
        background: "rgba(0,0,0,0.55)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
        opacity: shown ? 1 : 0,
        transition: "opacity 0.18s ease",
        WebkitTapHighlightColor: "transparent",
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        style={{
          width: "100%", maxWidth: 440,
          background: "#fff", borderRadius: 18,
          boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
          padding: 24,
          transform: shown ? "translateY(0) scale(1)" : "translateY(8px) scale(0.98)",
          transition: "transform 0.2s cubic-bezier(0.22,1,0.36,1)",
        }}
      >
        <div style={{ fontSize: 20, fontWeight: 800, color: "#111", marginBottom: 12 }}>
          Заказ не оплачен
        </div>

        <div style={{ fontSize: 15, lineHeight: 1.5, color: "#222", marginBottom: 8 }}>
          «<b>{title}</b>» уже оформлена в заказе <b>№{orderNumber}</b>
          {status ? ` (статус: ${status})` : ""}, но оплата не была завершена.
        </div>
        <div style={{ fontSize: 15, lineHeight: 1.5, color: "#222", marginBottom: 20 }}>
          Нажмите <b>«Повторить оплату»</b>, чтобы вернуться к оплате этого заказа.
        </div>

        {error && (
          <div style={{ fontSize: 14, color: "#d32f2f", marginBottom: 16 }}>
            {error}
          </div>
        )}

        <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
          <button
            type="button"
            onClick={() => !loading && onResult(false)}
            disabled={loading}
            style={{
              padding: "10px 18px", borderRadius: 12, border: "none",
              background: "#eceff1", color: "#333", fontSize: 15, fontWeight: 600,
              cursor: loading ? "not-allowed" : "pointer",
            }}
          >
            Отмена
          </button>
          <button
            type="button"
            onClick={retry}
            disabled={loading}
            style={{
              padding: "10px 22px", borderRadius: 12, border: "none",
              background: ACCENT, color: "#fff", fontSize: 15, fontWeight: 700,
              cursor: loading ? "wait" : "pointer",
              opacity: loading ? 0.75 : 1,
              transition: "opacity 0.15s ease",
            }}
          >
            {loading ? "Переходим…" : "Повторить оплату"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function confirmRetryPaymentModal({ title, orderNumber, status, onRetry }) {
  return new Promise((resolve) => {
    const host = document.createElement("div");
    document.body.appendChild(host);

    const root = createRoot(host);
    let resolved = false;
    const finish = (result) => {
      if (resolved) return;
      resolved = true;
      root.unmount();
      host.remove();
      resolve(result);
    };

    root.render(
      <RetryPaymentDialog
        title={title}
        orderNumber={orderNumber}
        status={status}
        onRetry={onRetry}
        onResult={finish}
      />
    );
  });
}